import React, { useState, useEffect } from 'react';
import Loader from '../../components/Loader';
import Calling from "../../api/Calling";
import { toast } from 'react-toastify';
import generateCaptcha from '../../components/captcha';
import FileUpload from '../../components/ul/FileUpload';
import VideoNavbar from './VideoNavbar';

const VideoCategory = () => {
    const [loader, setLoader] = useState(false);
    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState('');
    const [categoryImage, setCategoryImage] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editCategoryId, setEditCategoryId] = useState('');
    const [editCategoryName, setEditCategoryName] = useState('');
    const [editCategoryImage, setEditCategoryImage] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [editPreviewImage, setEditPreviewImage] = useState(null);
    const [categorySearch, setCategorySearch] = useState('');

    // Load all data on component mount
    useEffect(() => {
        fetchAllData();
    }, []);

    const fetchAllData = async () => {
        setLoader(true);
        try {
            const catRes = await Calling.getAllCategories();
            if (catRes.status) setCategories(catRes.data);
        } catch (error) {
            toast.error('Something went wrong!');
        } finally {
            setLoader(false);
        }
    };

    const validateImage = (file) => {
        const validTypes = ['image/png', 'image/gif', 'image/jpg', 'image/jpeg'];

        if (!validTypes.includes(file.type)) {
            toast.error('Only PNG, GIF, JPG, JPEG images are allowed');
            return false;
        }

        if (file.size > 1048576) {
            toast.error('Image size must be less than 1MB');
            return false;
        }

        return true;
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (validateImage(file)) {
                setCategoryImage(file);
                setPreviewImage(URL.createObjectURL(file));
            } else {
                e.target.value = null;
            }
        }
    };

    const handleEditImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (validateImage(file)) {
                setEditCategoryImage(file);
                setEditPreviewImage(URL.createObjectURL(file));
            } else {
                e.target.value = null;
            }
        }
    };

    const handleAddCategory = async () => {
        if (!newCategory.trim()) return toast.warning('Enter category name');
        if (!categoryImage) return toast.warning('Please upload main image');

        const formData = new FormData();
        formData.append('name', newCategory.trim());
        formData.append('image', categoryImage);

        setLoader(true);
        try {
            const res = await Calling.insertCategory(formData);
            if (res.status) {
                toast.success('Category added');
                setNewCategory('');
                setCategoryImage(null);
                setPreviewImage(null);
                fetchAllData();
            } else {
                toast.error(res.message || 'Failed to add category');
            }
        } catch (error) {
            toast.error('Something went wrong!');
        } finally {
            setLoader(false);
        }
    };

    const handleEditCategory = (category) => {
        setEditMode(true);
        setEditCategoryId(category._id);
        setEditCategoryName(category.name);

        if (category.main_image) {
            setEditPreviewImage(category.main_image);
        }

        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    const handleUpdateCategory = async () => {
        if (!editCategoryName.trim()) return toast.warning('Enter category name');

        const formData = new FormData();
        formData.append('name', editCategoryName.trim());
        formData.append('id', editCategoryId);
        if (editCategoryImage) {
            formData.append('image', editCategoryImage);
        }

        setLoader(true);
        try {
            const res = await Calling.updateCategory(formData);
            if (res.status) {
                toast.success('Category updated');
                resetForm();
                fetchAllData();
            } else {
                toast.error(res.message || 'Failed to update category');
            }
        } catch (error) {
            toast.error('Something went wrong!');
        } finally {
            setLoader(false);
        }
    };

    const resetForm = () => {
        setEditMode(false);
        setEditCategoryId('');
        setEditCategoryName('');
        setEditCategoryImage(null);
        setEditPreviewImage(null);
    };

    const handleDeleteCategory = async (id) => {
        const content = `Deleting this Product Category may remove linked subcategories and products. Solve the math problem to confirm.`;
        const check = generateCaptcha(content);
        if (check) {
            setLoader(true);
            try {
                const res = await Calling.deleteCategory(id);
                if (res.status) {
                    toast.success('Category deleted');
                    fetchAllData();
                } else {
                    toast.error(res.message || 'Failed to delete category');
                }
            } catch (error) {
                toast.error('Something went wrong!');
            } finally {
                setLoader(false);
            }
        } else {
            toast.error("Answer is not correct", { autoClose: 2000 });
        }
    };

    // Filter categories based on search term
    const filteredCategories = categories.filter(cat =>
        cat.name.toLowerCase().includes(categorySearch.toLowerCase())
    );

    // Handle image removal
    const handleRemoveImage = (isEdit) => {
        if (isEdit) {
            setEditPreviewImage(null);
            setEditCategoryImage(null);
        } else {
            setPreviewImage(null);
            setCategoryImage(null);
        }
    };

    const handleUpdateStatus = async (id) => {
        setLoader(true);
        try {
            const resp = await Calling.updateCategoryStatus(id);
            if (resp?.status) {
                toast.success(resp?.message);
                fetchAllData();
            } else {
                toast.error(resp?.message);
            }
        } catch (error) {
            console.error("Status update error:", error.response?.data || error.message);
            toast.error("Failed to update status.");
        } finally {
            setLoader(false);
        }
    };

    return (
        <>
            {loader ? (
                <Loader loader={loader} />
            ) : (
                <div className='w-full text-main-font mt-20 px-2 md:px-3 md:ml-[4.4rem]'>
                    <div className="flex items-center justify-between">
                        <h1 className="text-xl md:text-2xl font-bold text-main-font mt-4 w-auto">
                            Video Management
                        </h1>
                    </div>

                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                        <VideoNavbar selectState={'category'} />
                        <hr className="border-gray-200" />
                        <div className="grid md:grid-cols-1 gap-8 p-6">
                            {/* Left Column - Categories */}
                            <div className="space-y-6">
                                {/* Category Form */}
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-100">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-xl font-bold text-gray-800 flex items-center justify-between w-full">
                                            <div className='flex items-center'>
                                                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                </svg>
                                                {editMode ? 'Edit Category' : 'Add Category'}
                                            </div>
                                            <p>
                                                Total: {categories.length || 0}
                                            </p>
                                        </h2>
                                        {editMode && (
                                            <button
                                                onClick={resetForm}
                                                className="text-gray-500 hover:text-gray-700"
                                                aria-label="Cancel edit"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label htmlFor="category-name" className="block text-sm font-medium text-gray-700 mb-1">Category Name</label>
                                            <input
                                                id="category-name"
                                                type="text"
                                                value={editMode ? editCategoryName : newCategory}
                                                onChange={(e) => editMode ? setEditCategoryName(e.target.value) : setNewCategory(e.target.value)}
                                                placeholder="Enter category name"
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                            />
                                        </div>

                                        {/* Image Upload Section */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Main Image</label>
                                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors">
                                                {(editMode ? editPreviewImage : previewImage) ? (
                                                    <div className="relative inline-block">
                                                        <img
                                                            src={editMode ? editPreviewImage : previewImage}
                                                            alt="Preview"
                                                            className="w-24 h-24 object-cover rounded-lg shadow-sm"
                                                        />
                                                        <button
                                                            onClick={() => handleRemoveImage(editMode)}
                                                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                                            aria-label="Remove image"
                                                        >
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <FileUpload
                                                        id={editMode ? "edit-image" : "image"}
                                                        name={editMode ? "edit-image" : "image"}
                                                        accept="image/png, image/gif, image/jpg, image/jpeg"
                                                        onChange={editMode ? handleEditImageChange : handleImageChange}
                                                        required={!editMode}
                                                        multiple={false}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex gap-3 pt-2">
                                            {editMode ? (
                                                <>
                                                    <button
                                                        onClick={handleUpdateCategory}
                                                        className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg transition duration-200 flex items-center justify-center gap-2 shadow-sm"
                                                    >
                                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                        Update Category
                                                    </button>
                                                    <button
                                                        onClick={resetForm}
                                                        className="px-4 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition duration-200"
                                                    >
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    onClick={handleAddCategory}
                                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg transition duration-200 flex items-center justify-center gap-2 shadow-sm"
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                                    </svg>
                                                    Add Category
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Category List */}
                                <div className="bg-white p-6 rounded-xl border border-gray-200">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-xl font-bold text-gray-800">Categories</h2>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                placeholder="Search categories..."
                                                value={categorySearch}
                                                onChange={(e) => setCategorySearch(e.target.value)}
                                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                aria-label="Search categories"
                                            />
                                            <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                            </svg>
                                        </div>
                                    </div>

                                    {filteredCategories.length === 0 ? (
                                        <div className="text-center py-8 text-gray-500">
                                            <svg className="w-12 h-12 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                            </svg>
                                            {categorySearch ? 'No categories found matching your search' : 'No categories added yet'}
                                        </div>
                                    ) : (
                                        <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
                                            {filteredCategories.map((cat) => (
                                                <div
                                                    key={cat._id}
                                                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                                                >
                                                    <div className="flex items-center space-x-4">
                                                        <div className="flex space-x-4">
                                                            {cat.main_image && (
                                                                <div className="w-12 h-12 rounded-lg overflow-hidden border-2 border-white shadow-sm">
                                                                    <img
                                                                        src={cat.main_image}
                                                                        alt={cat.name}
                                                                        className="w-full h-full object-cover"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <h3 className="font-semibold text-gray-800">{cat.name}</h3>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                                                    {cat?.total_products || 0} Video
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <label className="relative inline-flex items-center cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={cat?.status}
                                                                onChange={() => handleUpdateStatus(cat?._id)}
                                                                className="sr-only peer"
                                                            />
                                                            <div className="relative w-11 h-6 bg-gray-200 rounded-full peer 
                                                                peer-checked:after:translate-x-full peer-checked:after:border-white 
                                                                after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                                                                after:bg-white after:border-gray-300 after:border after:rounded-full 
                                                                after:h-5 after:w-5 after:transition-all dark:border-gray-600 
                                                                peer-checked:bg-main-blue"></div>
                                                        </label>
                                                        <button
                                                            onClick={() => handleEditCategory(cat)}
                                                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                                            title="Edit"
                                                            aria-label="Edit category"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            onClick={() => handleDeleteCategory(cat._id)}
                                                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                                            title="Delete"
                                                            aria-label="Delete category"
                                                        >
                                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style jsx global>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #888;
                    border-radius: 3px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #555;
                }
            `}</style>
        </>
    );
};

export default VideoCategory;